import React from "react";
import { InputMask } from "@react-input/mask";
import { isValid, format, parse } from "date-fns";
import { fullDateLongFormat } from "@common/helpers/date";
import { Popover, PopoverContent } from "@design-system/Popover/Popover";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import {
    parseDateOnly,
    toDateOnly,
} from "@design-system/DatePicker/DatePicker.lib";
import styles from "../SpreadsheetGrid.module.scss";
import { BaseCellProps } from "./index";

// Convert ISO date to display format for input
const getDisplayValue = (dateString: string | null) => {
    if (!dateString) {
        return "";
    }
    const date = parse(dateString, fullDateLongFormat, new Date());
    if (!isValid(date)) {
        return "Invalid date";
    }
    return dateString;
};

// DatePickerNext works with ISO (yyyy-MM-dd) date-only strings, while
// spreadsheet cells store dates as MM/dd/yyyy — convert at the boundary
// using the shared DatePicker date-only helpers.
const toIsoValue = (value: string | null): string | undefined => {
    if (!value) {
        return undefined;
    }
    const date = parse(value, fullDateLongFormat, new Date());
    return isValid(date) ? toDateOnly(date) : undefined;
};

const fromIsoValue = (isoDate: string | undefined): string => {
    const date = parseDateOnly(isoDate);
    return date ? format(date, fullDateLongFormat) : "";
};

export const DateCell: React.FC<BaseCellProps<string>> = ({
    value,
    onChange,
    isEditing,
    onBlur,
    inputRef,
    finishEditing,
}) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Store the raw input value immediately as user types
        onChange(e.currentTarget.value);
    };

    const handleDatePickerChange = (isoDate: string | undefined) => {
        finishEditing(fromIsoValue(isoDate));
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            onBlur();
        }
    };

    if (isEditing) {
        return (
            <div
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <InputMask
                    mask="__/__/____"
                    ref={inputRef}
                    className={styles.input}
                    replacement={{
                        _: /\d/,
                    }}
                    showMask
                    value={value ?? ""}
                    onChange={handleInputChange}
                    onKeyDown={handleInputKeyDown}
                />
                <Popover open>
                    <PopoverContent
                        align="start"
                        anchor={inputRef}
                        initialFocus={false}
                        aria-label="Choose a date"
                    >
                        <DatePicker
                            mode="day"
                            variant="embedded"
                            clearable={false}
                            value={toIsoValue(value)}
                            onValueChange={handleDatePickerChange}
                        />
                    </PopoverContent>
                </Popover>
            </div>
        );
    }

    // Display formatted date in read mode
    const displayValue = getDisplayValue(value);
    return <div>{displayValue}</div>;
};
